import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import {
    bindAutoClose,
    bindFocusTrap,
    bindPosition,
    Placement,
    Strategy,
} from '@cmps/cdk';
import { CmpsTemplatePortal, CmpsTemplatePortalHandle } from '@cmps/ng-uikit';
import { mdiCalendarText, mdiFlag, mdiFolderAccount } from '@mdi/js';
import { ResizeSensor } from 'css-element-queries';
import { extent, max, sum } from 'd3-array';
import { Axis, axisBottom, AxisDomain, axisLeft } from 'd3-axis';
import { easeLinear } from 'd3-ease';
import { scaleLinear, scaleTime } from 'd3-scale';
import { select, Selection } from 'd3-selection';
import { area, line } from 'd3-shape';
import { timeFormat } from 'd3-time-format';
import { active } from 'd3-transition';
import { addDays, differenceInDays } from 'date-fns';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { CoachingEventType } from '../coaching-base/general';
import { PeriodType } from '../widget/date-range.widget.model';

const textAnchor = 'text-anchor';
const strokeWidth = 'stroke-width';
const strokeDashArray = 'stroke-dasharray';
const lineExtra = 'line-extra';

export interface Rect {
    width?: any;
    height?: number;
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface LineAreaOptions {
    thresholds?: LineAreaThreshold;
    lineStrokeColor?: string;
    lineStrokeColor2?: string;
    eventDotsColor?: string;
    eventDotsColor2?: string;
    sgLineStrokeColor?: string;
    tLineStrokeColor?: string;
    tLineStrokeColor2?: string;
    gLineStrokeColor?: string;
    gLineStrokeColor2?: string;
    graphLabel?: string;
    errorLabel?: string;
    dataLabel?: (d: any) => string;
    yTickLabel?: (d: any) => any; // d3 format function
    xTickLabel?: (d: any) => any; // d3 format function
    showLegend?: boolean;
    showTinyCircles?: boolean;
    showEventCircles?: boolean;
    showGoalCircles?: boolean;
    pendingGoal?: boolean;
    showVerticalThresholdLines?: boolean;
    periodType?: PeriodType;
}

export interface LineAreaThreshold {
    xAxis?: ThresholdLineConfig[];
    yAxis?: ThresholdLineConfig[];
}
export interface ThresholdLineConfig {
    label?: string;
    value: number;
    strokeColor: string;
    aboveColor?: string;
    belowColor?: string;
    active?: boolean;
}

export interface EventTooltipData {
    eventId: number;
    commitmentStatusName: string;
    date: Date;
    eventTypeName: string;
    performer?: string;
    statusColour: string;
    eventTypeId: CoachingEventType;
    startTime: Date;
    endTime: Date;
}

export interface GoalTooltipData {
    goalValue: number;
    currentPctGoal: number;
    percentToTarget: number;
}

export interface GraphPoint {
    x: any;
    y: any;
    tooltipData?: EventTooltipData[] | GoalTooltipData | any;
}

export interface BaseLineGraphData {
    mainData?: GraphPoint[];
    supergroupData?: GraphPoint[];
    targetData?: GraphPoint[];
    goalData?: GraphPoint[];
    eventData?: GraphPoint[];
}

export interface GraphDefinition {
    data?: BaseLineGraphData;
    options?: LineAreaOptions;
    showSg?: boolean;
    showEmptyGraph?: boolean;
}

@Directive({
    selector: '[lineAreaChartEventCallout]',
})
export class LineAreaChartEventCalloutDirective {
    constructor(/** @internal */ public _templateRef: TemplateRef<any>) {}
}

@Directive({
    selector: '[lineAreaChartGoalCallout]',
})
export class LineAreaChartGoalCalloutDirective {
    constructor(/** @internal */ public _templateRef: TemplateRef<any>) {}
}

@Component({
    selector: 'ng-chart-line-area',
    template: ` <div #svgContainer></div>
        <ng-template
            #datapointTpl
            let-data="data"
        >
            <div class="ng-chart-line-area--data-point-callout--box">
                <span class="ng-chart-line-area--data-point-callout--label">{{
                    data
                }}</span>
            </div>
        </ng-template>`,
    exportAs: 'ngChartLineArea',
})
export class LineAreaComponent implements OnChanges, OnDestroy, AfterViewInit {
    @ContentChild(LineAreaChartEventCalloutDirective)
    /** @private */
    readonly _eventCallout: LineAreaChartEventCalloutDirective;

    /** @private */ _eventCalloutHdl: CmpsTemplatePortalHandle<{
        data: EventTooltipData;
    }>;

    private _eventCalloutCancelSubj: Subject<void>;

    @ContentChild(LineAreaChartGoalCalloutDirective)
    /** @private */
    readonly _goalCallout: LineAreaChartGoalCalloutDirective;

    /** @private */ _goalCalloutHdl: CmpsTemplatePortalHandle<{
        data: GoalTooltipData;
    }>;

    private _goalCalloutCancelSubj: Subject<void>;

    @ViewChild('datapointTpl')
    /** @private */
    readonly _datapointCallout: TemplateRef<string>;

    /** @private */ _datapointCalloutHdl: CmpsTemplatePortalHandle<{
        data: string;
    }>;

    private _datapointCalloutCancelSubj: Subject<void>;

    tooltipbound = false;

    @ViewChild('svgContainer', { static: true })
    private readonly _svgContainer: ElementRef<HTMLElement>;

    @Input() graphSet: GraphDefinition[];
    @Input() customHeight: number;
    @Input() customWidth: number;
    @Input() graphSetSGToggles: boolean[];
    @Input() isVariance: boolean;
    @Input() maxTooltipHeight: number;
    @Input() maxTooltipWidth: number;

    // default dimensions
    height = 350;
    width = 640;
    top = 20;
    right = 40;
    bottom = 20;
    left = 40;

    _options: LineAreaOptions;
    renderedValue = false;
    defaultFormatdtg: any = timeFormat('%b %d');

    allSgPaths: any[];
    gc: any; // graph config
    resizelistener: any;

    rect: Rect;
    initializedDOM = false;

    extentdata: any[];

    @Input()
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get rendered() {
        return this.renderedValue;
    }

    set rendered(val) {
        this.renderedValue = val;
        this.renderedChange.emit(this.renderedValue);
    }

    @Output() renderedChange = new EventEmitter();

    private _eventSub: Subscription;

    svg: Selection<SVGElement, any, any, any>;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        private readonly cdRef: ChangeDetectorRef,
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _portal: CmpsTemplatePortal,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        const d = this._el.nativeElement.getBoundingClientRect();

        this.setRect({
            width: d.width || this.width,
            height: this.customHeight || this.height,
            top: this.top,
            right: this.right,
            bottom: this.bottom,
            left: d.left || this.left,
        });

        this.initializedDOM = true;

        if (this.graphSet.length) {
            this.renderContainer(this.rect);
            this._render(this.graphSet);
        }

        this.resizelistener = new ResizeSensor(
            this._el.nativeElement,
            (dimensions) => {
                this.setRect({
                    height: this.customHeight || dimensions.height,
                    width: this.customWidth || dimensions.width,
                    bottom: this.bottom,
                });
                this.renderContainer(this.rect);
                this._render(this.graphSet);
            },
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            this.initializedDOM &&
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('graphSet') &&
            this.graphSet.length
        ) {
            this.renderContainer(this.rect);
            this._render(this.graphSet);
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('graphSetSGToggles')) {
            this.toggleSG();
        }
    }

    ngOnDestroy(): void {
        this._eventMouseOut();
        this._goalMouseOut();

        if (this._eventSub) {
            this._eventSub.unsubscribe();
        }
        if (this.resizelistener) {
            this.resizelistener.detach();
            this.resizelistener = null;
        }

        this.allSgPaths = null;
        this.gc = null;
        this.rect = null;
        this._options = null;
        this._eventSub = null;

        this.tooltipbound = false;
    }

    private renderContainer(rect: Rect): any {
        this._eventMouseOut();
        this._goalMouseOut();

        let container: any;

        // empty the graph only on the first (main) metric
        const svgcontdiv = select(this._svgContainer.nativeElement);
        const graphcont = svgcontdiv.select('.main-oa-variance-graph');

        if (graphcont.empty()) {
            container = svgcontdiv.append('svg');

            container
                .attr('class', 'main-oa-variance-graph')
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .attr('version', '1.1')
                .attr('tabindex', '-1')
                .attr('width', rect.width - 5)
                .attr('height', rect.height + rect.top + rect.bottom);
        } else {
            graphcont
                .attr('width', rect.width - 5)
                .attr('height', rect.height + rect.top + rect.bottom);
            container = graphcont;
        }

        // SCALE BY GIVEN DIMENSIONS
        const x = scaleTime().range([0, rect.width - rect.left - rect.right]);
        const y = scaleLinear().range([rect.height, 0]);
        this.gc = { x, y, container };

        // ADD MAIN GRAPH (one per metric)
        this.gc.container.select('g').remove();
        this.svg = this.gc.container
            .append('g')
            .attr(
                'transform',
                `translate(${this.rect.left}, ${this.rect.top})`,
            );
    }

    private getDomains(graphSet: GraphDefinition[]): any {
        this.extentdata = [];

        graphSet.forEach((g) => {
            if (g.data) {
                const pdata = g.data.mainData;
                this.extentdata = [...this.extentdata, ...pdata];
                if (g.data.supergroupData)
                    this.extentdata = [
                        ...this.extentdata,
                        ...g.data.supergroupData,
                    ];
                if (g.data.targetData)
                    this.extentdata = [
                        ...this.extentdata,
                        ...g.data.targetData,
                    ];
                if (g.data.goalData)
                    this.extentdata = [...this.extentdata, ...g.data.goalData];
                if (g.data.eventData)
                    this.extentdata = [...this.extentdata, ...g.data.eventData];
            }
        });

        return this.setDomains();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private setDomains() {
        let xdomain;
        let ydomain;

        if (this.isVariance) {
            // variance
            xdomain = extent(this.extentdata, function (d: any) {
                return d.x;
            });
            ydomain = [
                max(this.extentdata, function (d: any) {
                    return Math.abs(d.y != null ? d.y : 1);
                }) * -1,
                max(this.extentdata, function (d: any) {
                    return Math.abs(d.y != null ? d.y : 1);
                }),
            ];
        } else {
            // trend
            xdomain = extent(this.extentdata, function (d: any) {
                return d.x;
            });
            ydomain = extent(this.extentdata, function (d: any) {
                return d.y;
            });
            const thresholdYScale = this.gc.y.copy().domain(ydomain).nice();
            const ticks = thresholdYScale.ticks();
            if (ticks.length >= 2) {
                ydomain[0] = ticks[0] - ticks[1];
            }
        }
        return {
            xdomain,
            ydomain,
        };
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private setRect(rect?: Rect) {
        if (!rect) {
            rect = {
                width: this.width,
                height: this.customHeight || this.height,
                top: this.top,
                right: this.right,
                bottom: this.bottom,
                left: this.left,
            };
        }

        this.rect = {
            top: this.top,
            right: this.right,
            bottom: this.bottom,
            left: this.left,
            ...rect,
        };
    }

    private _render(g: GraphDefinition[]): void {
        if (!g || !g.length || !g[0].data) return;

        this.rendered = false;

        const domains = this.getDomains(g);
        this.allSgPaths = [];

        g.forEach((definition: GraphDefinition, idx: number) => {
            this.__renderGraphDef(definition, domains, idx, g.length);
        });

        this.rendered = true;

        if (this.graphSetSGToggles) {
            this.toggleSG();
        }

        this.cdRef.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private __renderGraphDef(
        definition,
        domains,
        idx: number,
        glength: number,
    ) {
        const data = definition.data;
        const options = definition.options;

        const performerData: GraphPoint[] = data.mainData;
        const supergroupData: GraphPoint[] = data.supergroupData;
        const targetData: GraphPoint[] = data.targetData;
        const goalData: GraphPoint[] = data.goalData;
        const eventData: GraphPoint[] = data.eventData;

        const clipPaths: any[] = [];

        if (!options || !options.thresholds) {
            return;
        }

        const timeinterval = this.getTimeInterval();
        const domain = domains.xdomain;
        const lastDate = domain[domain.length - 1];
        const ticks: Date[] = [];
        let d1 = domain[0];
        do {
            ticks.push(d1);
            d1 = addDays(d1, timeinterval);
        } while (d1 <= lastDate);
        if (
            Math.abs(differenceInDays(ticks[ticks.length - 1], lastDate)) <=
            timeinterval / 2
        ) {
            ticks.splice(ticks.length - 1, 1);
        }
        ticks.push(lastDate);

        const thresholdsX: ThresholdLineConfig[] =
            options.thresholds.xAxis || [];
        const thresholdsY: ThresholdLineConfig[] =
            options.thresholds.yAxis || [];
        const xAxis = axisBottom(this.gc.x).tickValues(ticks);
        const yAxis = axisLeft(this.gc.y);

        // Primary Lines
        const mainline = area()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));
        // .curve(d3.curveBasis);

        const sgline = line()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));

        const tline = line()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));

        const gline = line()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));

        const area1 = area()
            .x((d: any) => this.gc.x(d.x))
            .y1((d: any) => this.gc.y(d.y));

        const thresholdXScale = this.gc.x.domain(domains.xdomain);
        const thresholdYScale = this.gc.y.domain(domains.ydomain).nice();

        this.svg.datum(performerData);

        // ADD SHADED AREAS BY Y-AXIS THRESHOLDS
        this.svg
            .append('clipPath')
            .attr('id', 'clip-above')
            .append('path')
            .attr('d', area1.y0(this.rect.height));

        this.svg
            .append('clipPath')
            .attr('id', 'clip-below')
            .append('path')
            .attr('d', area1.y0(0));

        this.addShadedAreasThresholdY(thresholdsY, clipPaths, area1);

        // the performer line
        const { performerPath, tPath, gPath } = this.addPaths({
            targetData,
            supergroupData,
            goalData,
            mainline,
            tline,
            sgline,
            gline,
            options,
        });

        if (idx === glength - 1) {
            this.appendGridLines();
        }

        if (idx === glength - 1) {
            this.appendAxes(xAxis, yAxis, options);
        }

        // LABEL OF GRAPH
        if (options.graphLabel) {
            this.addGraphLabel(options);
        }

        this.addHorizontalPaths(
            thresholdsY,
            thresholdXScale,
            thresholdYScale,
            domains,
        );

        if (this.rendered === false) {
            this.animatePaths(performerPath, tPath, gPath, clipPaths, idx);
        }

        this.appendDataPoints(
            { performerData, targetData, supergroupData, goalData, eventData },
            idx,
            options,
        );

        if (!options.showVerticalThresholdLines) return;

        // ADD VERTICAL DOTTED THRESHOLD LINES
        if (idx === glength - 1) {
            // only with last graph def
            this.addVerticalPaths(thresholdsX, thresholdXScale);
        }
    }

    getTimeInterval(): number {
        // POSITION AXIS LABELS
        let timeinterval = 3;

        this.extentdata.sort((a, b) => {
            return a.x - b.x;
        });

        if (this.extentdata.length >= 40 && this.extentdata.length < 70) {
            timeinterval = 6;
        } else if (
            this.extentdata.length < 100 &&
            this.extentdata.length >= 70
        ) {
            timeinterval = 10;
        } else if (
            this.extentdata.length < 200 &&
            this.extentdata.length > 100
        ) {
            timeinterval = 15;
        } else if (this.extentdata.length >= 200) {
            timeinterval = 20;
        } else if (
            this.extentdata.length < 40 &&
            this.extentdata.length >= 10
        ) {
            timeinterval = 25;
        } else if (this.extentdata.length < 10) {
            timeinterval = 28;
        }

        const startdate = this.extentdata[0].x;
        const enddate = this.extentdata[this.extentdata.length - 1].x;
        const diff = Math.abs(enddate - startdate);
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        if (days > 60 && days < 201) {
            timeinterval = 30;
        } else if (days > 200 && days < 401) {
            timeinterval = 60;
        } else if (days > 400) {
            timeinterval = 90;
        }

        return timeinterval;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addPaths({
        targetData,
        supergroupData,
        goalData,
        mainline,
        tline,
        sgline,
        gline,
        options,
    }) {
        // the performer line
        let performerPath: any = null;
        // the SG Average line
        let sgPath: any = null;
        // the Target line
        let tPath: any = null;
        // the Goal line
        let gPath: any = null;

        // ADD THE LINE GRAPH

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (supergroupData && supergroupData.length) {
            // supergroup average line
            sgPath = this.svg.append('path');
            sgPath
                .datum(supergroupData)
                .attr('class', lineExtra)
                .style('fill', 'none')
                .style('stroke', options.sgLineStrokeColor || '#000')
                .style(strokeWidth, '3')
                .style(strokeDashArray, '3 4')
                .style('display', 'none')
                .attr('d', sgline);
            this.allSgPaths.push(sgPath);
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (goalData && goalData.length) {
            // goal line
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const id = Math.floor(Math.random() * 100);

            gPath = this.svg.append('path');
            gPath
                .datum(goalData)
                .attr('class', 'line')
                .style('fill', 'none')
                .attr('opacity', options.pendingGoal ? 0.5 : 1)
                .style('display', 'none')
                .style('stroke', options.gLineStrokeColor || '#000')
                .style(strokeWidth, '2.5')
                .attr('d', gline);
        }

        // performer line
        performerPath = this.svg
            .append('path')
            .attr('class', 'line')
            .style('stroke', options.lineStrokeColor || '#000')
            .style(strokeWidth, '4')
            .attr('d', mainline);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (targetData && targetData.length) {
            // target line
            tPath = this.svg.append('path');
            tPath
                .datum(targetData)
                .attr('class', lineExtra)
                .style('fill', 'none')
                .style('stroke', options.tLineStrokeColor || '#000')
                // .style(strokeDashArray, '7')
                .style(strokeWidth, '3')
                .attr('opacity', 0.9)
                .attr('d', tline);
        }

        return {
            performerPath,
            tPath,
            gPath,
        };
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    animatePaths(performerPath, tPath, gPath, clipPaths, idx) {
        let d = 500;

        const pathNodes = [
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            {
                name: 'data',
                path: performerPath,
                length: performerPath.node().getTotalLength(),
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                dots: `.data-circle${idx}`,
            },
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/restrict-template-expressions
            {
                name: 'goal-data',
                path: gPath,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                length: gPath && gPath.node().getTotalLength(),
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                dots: `.data-circle${idx}`,
            },
        ];

        pathNodes.forEach((p) => {
            if (gPath == null) {
                return;
            }
            let dashlength = p.length;
            let dir = -dashlength;

            if (p.name === 'goal') {
                dir = 0;
                dashlength = 5;
            }

            if (p.name === 'data') {
                console.log('dash', p.name, dashlength);

                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                p.path
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .attr(strokeDashArray, `${dashlength} ${dashlength}`)
                    .attr('stroke-dashoffset', dir)
                    .transition()
                    .on('end', () => {
                        this.svg.selectAll(p.dots).style('display', 'block');
                        this.svg
                            .selectAll(`${p.dots}-icon`)
                            .style('display', 'block');
                    })
                    .duration(1500)
                    .ease(easeLinear)
                    .attr('stroke-dashoffset', 0);
            }

            // animate line with dashed stroke for goal data
            if (p.name === 'goal-data') {
                const dashArray = [8, 2];
                const dashArrayLength = sum(dashArray);
                p.path
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .attr('stroke-dasharray', `0 ${dashlength}`)
                    .transition()
                    .on('start', function repeat() {
                        active(this)
                            .transition()
                            .duration(1500)
                            .attrTween('stroke-dasharray', function () {
                                return function (t) {
                                    const tweenPathLength = dashlength * t;
                                    const repeatCounter = Math.floor(
                                        tweenPathLength / dashArrayLength,
                                    );
                                    const array = dashArray
                                        .join(' ')
                                        .concat(' ')
                                        .repeat(repeatCounter);
                                    const finalStripe = Math.min(
                                        tweenPathLength -
                                            repeatCounter * dashArrayLength,
                                        dashArray[0],
                                    );

                                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                    return array + [finalStripe, dashlength];
                                };
                            })
                            .delay(d)
                            .transition()
                            .duration(1000);
                    })
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .attr('stroke-dasharray', `0 ${dashlength}`);
            }
        });

        // animate the target path separately
        if (tPath) {
            d += 500;
            setTimeout(() => {
                tPath.style('display', 'block');
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                this.svg
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .selectAll(`.target-circle${idx}`)
                    .style('display', 'block');
            }, d);
        }

        // if in variance, show the shaded areas
        setTimeout(() => {
            clipPaths.forEach((cp) => {
                cp.attr('opacity', this.isVariance ? 0.9 : 0.3).style(
                    'transform',
                    'translateX(0rem)',
                );
            });
        }, d - 200);

        if (gPath) {
            setTimeout(() => {
                gPath.style('display', 'block');
                setTimeout(() => {
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    this.svg
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        .selectAll(`.goal-circle${idx}`)
                        .style('display', 'block');
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    this.svg
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        .selectAll(`.goal-circle${idx}-icon`)
                        .style('display', 'block');
                }, 1000);
            }, d);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addGraphLabel(ops) {
        this.svg
            .append('text')
            .attr('x', this.rect.width / 2)
            .attr('y', this.rect.height + this.rect.bottom - 5)
            .attr(textAnchor, 'middle')
            .style('font-size', '14px')
            .attr('class', 'shadow')
            .text(ops.graphLabel);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addShadedAreasThresholdY(thresholdsY, clipPaths, area1) {
        thresholdsY.forEach((d) => {
            clipPaths.push(
                this.svg
                    .append('path')
                    .attr('class', 'area below')
                    .style('fill', d.belowColor || 'transparent')
                    .attr('opacity', 0)
                    .style('transform', 'translateX(-0.5rem)')
                    .attr('clip-path', 'url(#clip-below)')
                    .attr('d', area1.y0(this.gc.y(d.value))),
            );

            clipPaths.push(
                this.svg
                    .append('path')
                    .attr('class', 'area above')
                    .style('fill', d.aboveColor || 'transparent')
                    .attr('opacity', 0)
                    .style('transform', 'translateX(-0.5rem)')
                    .attr('clip-path', 'url(#clip-above)')
                    .attr('d', area1),
            );
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addHorizontalPaths(thresholdsY, thresholdXScale, thresholdYScale, domains) {
        // ADD HORIZONTAL DOTTED THRESHOLD LINES
        thresholdsY.forEach((d: any) => {
            this.svg
                .append('line')
                .style('stroke', d.strokeColor)
                .style(strokeWidth, '2px')
                .style(strokeDashArray, '5 5')
                .attr('x1', thresholdXScale(domains.xdomain[0]))
                .attr('y1', thresholdYScale(d.value))
                .attr('x2', thresholdXScale(domains.xdomain[1]))
                .attr('y2', thresholdYScale(d.value))
                .attr('class', 'zeroline-y');
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addVerticalPaths(thresholdsX, thresholdXScale) {
        // ADD VERTICAL DOTTED THRESHOLD LINES
        thresholdsX.forEach((d: any) => {
            this.svg
                .append('line')
                .style('stroke', d.strokeColor)
                .style(strokeWidth, '2px')
                .style(strokeDashArray, '3')
                .attr('opacity', 0.5)
                .attr('x1', thresholdXScale(d.value))
                .attr('y1', 0)
                .attr('x2', thresholdXScale(d.value))
                .attr('y2', this.rect.height)
                .attr('class', 'zeroline-x');
        });
    }

    // gridlines in x axis function
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    makeXGridlines(x) {
        return axisBottom(x);
    }

    // gridlines in y axis function
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    makeYGridlines(y) {
        return axisLeft(y);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    defaultDataLabel(data: any) {
        const date = this.defaultFormatdtg(data.x);
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `${date}: ${data.y}`;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    defaultYTickLabel() {
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    defaultXTickLabel() {
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendGridLines() {
        // add vertical gridlines
        this.svg
            .append('g')
            .attr('class', 'gridX')
            .attr('stroke-opacity', '0.1')
            .attr('transform', `translate(0, ${this.rect.height})`)
            .call(
                this.makeXGridlines(this.gc.x)
                    .tickSize(-this.rect.height)
                    .tickFormat(() => ''),
            );

        // add horizontal gridlines
        this.svg
            .append('g')
            .attr('class', 'gridY')
            .attr('stroke-opacity', '0.1')
            .call(
                this.makeYGridlines(this.gc.y)
                    .tickSize(-this.rect.width)
                    .tickFormat(() => ''),
            );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendAxes(
        xAxis: Axis<AxisDomain>,
        yAxis: Axis<AxisDomain>,
        options: LineAreaOptions,
    ) {
        this.svg
            .append('g') // x axis
            .attr('class', 'x axis')
            .attr('transform', `translate(0, ${this.rect.height})`)
            .call(
                xAxis.tickFormat(
                    options.xTickLabel
                        ? options.xTickLabel
                        : this.defaultXTickLabel,
                ),
            );

        this.svg
            .append('g') // y axis
            .attr('class', 'y axis')
            .call(
                yAxis.tickFormat(
                    options.yTickLabel
                        ? options.yTickLabel
                        : this.defaultYTickLabel,
                ),
            )
            .append('text')
            .attr('transform', `translate(5, ${this.rect.height}) rotate(-90)`)
            .attr('dy', '.71em')
            .style(textAnchor, 'start')
            .attr('class', 'shadow');
    }

    appendDataPoints(
        dotsForData: { [key: string]: GraphPoint[] },
        idx: number,
        options: LineAreaOptions,
    ): void {
        // ADD DATA POINT CIRCLES AND THEIR HOVER EVENTS
        for (const prop in dotsForData) {
            const dataset = dotsForData[prop];
            if (!dataset) continue;

            let fillcolor = '';
            let fillcolor2 = '';
            let circleclass = '';

            if (prop === 'eventData') {
                fillcolor = options.eventDotsColor || '#000';
                fillcolor2 = options.eventDotsColor2 || '#000';
                circleclass = `event-circle${idx}`;

                const gs = this.svg
                    .selectAll('line-circle')
                    .data(dataset)
                    .enter()
                    .append('g');

                gs.append('circle')
                    .attr('class', circleclass)
                    .attr('fill', (d, i) =>
                        d.tooltipData && d.tooltipData.length > 1
                            ? fillcolor2
                            : fillcolor,
                    )
                    .attr('stroke', 'none')
                    .attr('opacity', 0.9)
                    .attr('r', 7)
                    .attr('cx', (d) => {
                        return this.gc.x(d.x);
                    })
                    .attr('cy', (d) => {
                        return this.gc.y(d.y);
                    })
                    .style('display', 'block')
                    .on('mouseover', (d, i, g) =>
                        this._eventMouseOver(select(g[i]), d),
                    );

                gs.append('svg')
                    .attr('x', (d) => {
                        return this.gc.x(d.x) - 5;
                    })
                    .attr('y', (d) => {
                        return this.gc.y(d.y) - 5;
                    })
                    .attr('width', 10)
                    .attr('height', 10)
                    .attr('pointer-events', 'none')
                    .attr('viewBox', '0 0 24 24')
                    .attr('fill', '#fff')
                    .append('path')
                    .attr('d', (d) => {
                        if (d.tooltipData?.length === 1) {
                            return mdiFolderAccount;
                        }
                        if (d.tooltipData?.length > 1) {
                            return mdiCalendarText;
                        }
                        return '';
                    });
            } else if (prop === 'goalData') {
                fillcolor = options.gLineStrokeColor || '#000';
                fillcolor2 = options.gLineStrokeColor2 || '#000';
                circleclass = `goal-circle${idx}`;
                const goalmax = dataset.length;

                // const opacity = prop === 'performerData' ? 0.9 : 0.6;

                const gs = this.svg
                    .selectAll('line-circle')
                    .data(dataset)
                    .enter()
                    .append('g');

                // do not draw black dot on initial datapoint
                gs.append('circle')
                    .attr('class', circleclass)
                    .attr('fill', (dataitem, i) =>
                        goalmax === i + 1 ? fillcolor : fillcolor2,
                    )
                    .attr('stroke', 'none')
                    .attr('opacity', options.pendingGoal ? 0.5 : 0.9)
                    .attr('r', (dataitem, i) => {
                        if (goalmax === i + 1) {
                            return 10;
                        } else {
                            return 4;
                        }
                    })
                    .attr('cx', (d) => {
                        return this.gc.x(d.x);
                    })
                    .attr('cy', (d) => {
                        return this.gc.y(d.y);
                    })
                    .style('display', 'none')
                    .style('pointer-events', (d, i) =>
                        goalmax === i + 1 ? 'auto' : 'none',
                    )
                    .filter((d, i) => goalmax === i + 1)
                    .on('mouseover', (d, i, g) =>
                        this._goalMouseOver(select(g[i]), d),
                    )
                    .on('mouseout', () => this._goalMouseOut());

                gs.filter((d, i) => goalmax === i + 1)
                    .append('svg')
                    .attr('class', `${circleclass}-icon`)
                    .attr('x', (d) => {
                        return this.gc.x(d.x) - 7;
                    })
                    .attr('y', (d) => {
                        return this.gc.y(d.y) - 7;
                    })
                    .attr('width', 14)
                    .attr('height', 14)
                    .style('display', 'none')
                    .style('pointer-events', 'none')
                    .attr('viewBox', '0 0 24 24')
                    .attr('fill', '#fff')
                    .append('path')
                    .attr('d', mdiFlag);
            } else if (prop === 'performerData') {
                fillcolor = '#fff';
                circleclass = `data-circle${idx}`;

                const opacity = !options.showTinyCircles ? 0 : 0.9;

                this.svg
                    .selectAll('line-circle')
                    .data(dataset)
                    .enter()
                    .append('circle')
                    .attr('class', circleclass)
                    .attr('fill', fillcolor)
                    .attr('stroke', options.lineStrokeColor)
                    .attr('opacity', opacity)
                    .attr('r', 3)
                    .attr('cx', (d) => {
                        return this.gc.x(d.x);
                    })
                    .attr('cy', (d) => {
                        return this.gc.y(d.y);
                    })
                    .style('display', 'none')
                    .on('mouseover', (a, b, c) => {
                        this._handleMouseOverDot(b, c, { dataset, options });
                    })
                    .on('mouseout', (a, b, c) => {
                        this._handleMouseOutDot(b, c, fillcolor);
                    });
            }
        }
    }

    private _handleMouseOverDot(
        b: number,
        c: SVGCircleElement[] | ArrayLike<SVGCircleElement>,
        opts: { dataset: GraphPoint[]; options: LineAreaOptions },
    ): void {
        const { dataset, options } = opts;

        const el = c[b] as Element as HTMLElement;

        const d = dataset[b];

        this._eventMouseOut();

        const label = options.dataLabel
            ? options.dataLabel(d)
            : this.defaultDataLabel(d);
        const { hdl, cancelSubj } = this._makePortal<string>(
            this._datapointCallout,
            label,
            el,
            'top-left',
        );
        this._datapointCalloutHdl = hdl;
        this._datapointCalloutCancelSubj = cancelSubj;

        const elSel = select(el);
        elSel.interrupt();
        elSel.transition().duration(250).attr('fill', options.lineStrokeColor);

        const selector = [
            'path.area.below',
            'path.area.above',
            'path.line-extra:not([id^="gPath-"])',
            'line.zeroline-x',
            'line.zeroline-y',
            'circle.goal-circle0',
            'image.goal-circle0-icon',
        ].join(',');
        this.svg.selectAll(selector).attr('opacity', 0.1);

        this.svg
            .selectAll('path.line-extra[id^="gPath-"]')
            .attr('opacity', 0.3);
    }

    private _handleMouseOutDot(
        b: number,
        c: SVGCircleElement[] | ArrayLike<SVGCircleElement>,
        fillColor: string,
    ): void {
        if (this._datapointCalloutCancelSubj) {
            this._datapointCalloutCancelSubj.next();
            this._datapointCalloutCancelSubj.complete();
        }
        this._datapointCalloutCancelSubj = null;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._datapointCalloutHdl && this._datapointCalloutHdl.destroy();
        this._datapointCalloutHdl = null;

        const elSel = select(c[b]);
        elSel.interrupt();
        elSel.transition().duration(250).attr('fill', fillColor);

        let selector = ['path.area.below', 'path.area.above'].join(',');
        this.svg
            .selectAll(selector)
            .attr('opacity', this.isVariance ? 0.9 : 0.3);

        selector = [
            'path.line-extra',
            'line.zeroline-x',
            'line.zeroline-y',
            'circle.goal-circle0',
            'image.goal-circle0-icon',
        ].join(',');
        this.svg.selectAll(selector).attr('opacity', 0.9);
    }

    private _makePortal<T>(
        templateRef: TemplateRef<any>,
        data: T,
        target: HTMLElement,
        placement: Placement,
    ): {
        hdl: CmpsTemplatePortalHandle<{ data: T }>;
        cancelSubj: Subject<void>;
    } {
        const hdl = this._portal.create<{ data: T }>(
            templateRef,
            this._viewContainerRef,
            { data },
        );
        hdl.ref.detectChanges();

        const cancelSubj = new Subject<void>();

        /// //
        const posFn = bindPosition(
            Strategy.ADJACENT,
            hdl.wrapper,
            target,
            placement,
            (pos) => {
                Object.keys(pos.style).forEach((k) => {
                    hdl.wrapper.style[k] = pos.style[k];
                });
                hdl.wrapper.style.zIndex = '1049';
            },
            cancelSubj,
        );
        this._ngZone.onStable
            .pipe(
                tap(() => {
                    // bindPosition should support padding the size of the target rect
                    if (target.tagName.toLowerCase() === 'circle') {
                        target.setAttribute(
                            'r',
                            `${parseInt(target.getAttribute('r')) + 1}`,
                        );
                    }
                    posFn();
                    // bindPosition should support padding the size of the target rect
                    if (target.tagName.toLowerCase() === 'circle') {
                        target.setAttribute(
                            'r',
                            `${parseInt(target.getAttribute('r')) - 1}`,
                        );
                    }
                }),
                takeUntil(cancelSubj),
            )
            .subscribe();
        /// //

        bindAutoClose(
            'click|escape',
            hdl.wrapper,
            [target, hdl.wrapper],
            () => this._eventMouseOut(),
            cancelSubj,
        );
        bindFocusTrap(hdl.wrapper, false, cancelSubj);

        return { hdl, cancelSubj };
    }

    private _eventMouseOver(
        el: Selection<SVGCircleElement, any, any, any>,
        d: GraphPoint,
    ): void {
        this._eventMouseOut();

        const target = el.node() as Element as HTMLElement;

        const { hdl, cancelSubj } = this._makePortal<EventTooltipData>(
            this._eventCallout._templateRef,
            d.tooltipData,
            target,
            'bottom-right',
        );
        this._eventCalloutHdl = hdl;
        this._eventCalloutCancelSubj = cancelSubj;
    }

    private _eventMouseOut(): void {
        if (this._eventCalloutCancelSubj) {
            this._eventCalloutCancelSubj.next();
            this._eventCalloutCancelSubj.complete();
        }
        this._eventCalloutCancelSubj = null;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._eventCalloutHdl && this._eventCalloutHdl.destroy();
        this._eventCalloutHdl = null;
    }

    private _goalMouseOver(
        el: Selection<SVGCircleElement, any, any, any>,
        d: GraphPoint,
    ): void {
        this._eventMouseOut();

        const target = el.node() as Element as HTMLElement;

        const { hdl, cancelSubj } = this._makePortal<GoalTooltipData>(
            this._goalCallout._templateRef,
            d.tooltipData,
            target,
            'top-right',
        );
        this._goalCalloutHdl = hdl;
        this._goalCalloutCancelSubj = cancelSubj;
    }

    private _goalMouseOut(): void {
        if (this._goalCalloutCancelSubj) {
            this._goalCalloutCancelSubj.next();
            this._goalCalloutCancelSubj.complete();
        }
        this._goalCalloutCancelSubj = null;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._goalCalloutHdl && this._goalCalloutHdl.destroy();
        this._goalCalloutHdl = null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleSG() {
        if (this.allSgPaths && this.graphSetSGToggles) {
            this.graphSetSGToggles.forEach((p, idx) => {
                const sgPath = this.allSgPaths[idx];

                if (sgPath) {
                    if (p === true) {
                        if (sgPath.style('display') === 'none') {
                            sgPath.style('display', 'block');
                            this.svg
                                .selectAll(`.sg-circle${idx}`)
                                .style('display', 'block');
                        }
                    } else {
                        sgPath.style('display', 'none');
                        this.svg
                            .selectAll(`.sg-circle${idx}`)
                            .style('display', 'none');
                    }
                }
            });
        }
    }
}
